/**
 * OperatorTakeoverPanel: the composer of a person who took over a run's
 * conversation (agion/Agion#3435). It says that the person replies as
 * themselves, sends each reply as mission.operator.message for the hold,
 * shows what the runner did with each one (accepted, or refused with the
 * reason), and hands the conversation back to the agent.
 *
 * The runner decides which reply it takes and governance whether the hold is
 * still the person's; the panel shows their answers. The text cap is the
 * contract's (MaxOperatorMessageChars, generated); the server checks it too.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, AlertTitle, Button, Text, Textarea } from '@agion/ui'
import { MaxOperatorMessageChars } from '@agion/mission-stream'
import { operatorReplyRefusalText } from '../../services/operator-takeover'
import type { OperatorReply } from '../../hooks/useStreamingMissionChat'

/** Hold states (Go missionstream OperatorHold*) after which the person can no longer reply. */
const ENDED_HOLD_STATES: ReadonlySet<string> = new Set(['returned', 'expired', 'unavailable'])

export interface OperatorTakeoverPanelProps {
  /** When governance ends the hold's lease, when it says so. */
  holdUntil?: string
  /** The hold's latest state from the run, when one arrived. */
  holdState?: string
  /** This person's replies in this hold, oldest first. */
  replies: OperatorReply[]
  onSend: (text: string) => void
  /** Hands the conversation back; resolves true when governance took it back. */
  onHandBack: () => Promise<boolean>
}

type Translate = (key: string, fallback: string) => string

function replyStatusText(t: Translate, reply: OperatorReply): string {
  switch (reply.status) {
    case 'sending':
      return t('chat:operatorTakeover.reply.sending', 'Sending…')
    case 'accepted':
      return t('chat:operatorTakeover.reply.accepted', 'Delivered to the customer')
    case 'not_sent':
      return t('chat:operatorTakeover.reply.notSent', 'Not sent: the connection is down.')
    case 'refused':
      return `${t('chat:operatorTakeover.reply.refused', 'Refused')}: ${operatorReplyRefusalText(t, reply.reason)}`
  }
}

export function OperatorTakeoverPanel({ holdUntil, holdState, replies, onSend, onHandBack }: OperatorTakeoverPanelProps) {
  const { t } = useTranslation(['chat'])
  const tr = t as unknown as Translate
  const [text, setText] = useState('')
  const [handingBack, setHandingBack] = useState(false)
  const [handBackFailed, setHandBackFailed] = useState(false)

  const holdEnded = holdState !== undefined && ENDED_HOLD_STATES.has(holdState)
  const holdUntilMs = holdUntil ? Date.parse(holdUntil) : NaN
  const canSend = !holdEnded && text.trim() !== ''

  const send = () => {
    if (!canSend) return
    onSend(text)
    setText('')
  }

  const handBack = async () => {
    setHandingBack(true)
    setHandBackFailed(false)
    const returned = await onHandBack()
    setHandingBack(false)
    if (!returned) setHandBackFailed(true)
  }

  return (
    <div className="max-w-3xl mx-auto space-y-2" data-testid="operator-takeover-panel">
      <Alert variant="info" role="status" data-testid="operator-takeover-banner">
        <AlertTitle>{t('chat:operatorTakeover.banner', 'You are replying as yourself to the customer')}</AlertTitle>
        {Number.isFinite(holdUntilMs) && (
          <AlertDescription data-testid="operator-takeover-hold-until">
            {t('chat:operatorTakeover.holdUntil', {
              time: new Date(holdUntilMs).toLocaleTimeString(),
              defaultValue: 'You hold this conversation until {{time}}.',
            })}
          </AlertDescription>
        )}
        {holdEnded && (
          <AlertDescription data-testid="operator-takeover-ended">
            {t('chat:operatorTakeover.ended', 'You no longer hold this conversation.')}
          </AlertDescription>
        )}
      </Alert>

      {replies.length > 0 && (
        <ul className="space-y-1" data-testid="operator-takeover-replies">
          {replies.map((reply) => (
            <li key={reply.messageId} data-testid="operator-takeover-reply" data-status={reply.status}>
              <Text size="xs" color="muted" className="block break-words">
                <span className="line-clamp-1">{reply.text}</span>
                <span role={reply.status === 'refused' || reply.status === 'not_sent' ? 'alert' : undefined}>
                  {replyStatusText(tr, reply)}
                </span>
              </Text>
            </li>
          ))}
        </ul>
      )}

      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={MaxOperatorMessageChars}
        rows={3}
        disabled={holdEnded}
        placeholder={t('chat:operatorTakeover.placeholder', 'Write to the customer')}
        aria-label={t('chat:operatorTakeover.placeholder', 'Write to the customer')}
        data-testid="operator-takeover-input"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            send()
          }
        }}
      />
      <div className="flex gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={send}
          disabled={!canSend}
          data-testid="operator-takeover-send"
        >
          {t('chat:operatorTakeover.send', 'Send')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void handBack()}
          disabled={handingBack}
          data-testid="operator-takeover-hand-back"
        >
          {handingBack
            ? t('chat:operatorTakeover.handingBack', 'Handing back…')
            : t('chat:operatorTakeover.handBack', 'Hand back')}
        </Button>
      </div>
      {handBackFailed && (
        <Text size="xs" role="alert" className="block text-red-600 dark:text-red-400" data-testid="operator-takeover-hand-back-error">
          {t('chat:operatorTakeover.handBackFailed', 'The conversation was not handed back. Try again.')}
        </Text>
      )}
    </div>
  )
}
